import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import {
  GetArtifactComponentQueryQuery,
  GetArtifactComponentQueryQueryVariables,
  GoalState,
} from "types/graphql-schema";

import Artifact from "@apps/artifact/artifact";
import getArtifactComponentQuery from "@apps/artifact/graphql/get-artifact-component-query";
import CheckinDialog from "@apps/checkin-dialog/checkin-dialog";
import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";

const AskAIEmbedGoalProgressUpdate = ({
  goalId,
  suggestedMessage,
  showCheckinButton = false,
}: {
  goalId?: number | null;
  suggestedMessage?: string | null;
  showCheckinButton?: boolean;
}) => {
  const label = useLabel();
  const { ref, inView } = useInView();
  const [updatedProgress, setUpdatedProgress] = useState<boolean>(false);
  const [isShowingCheckinDialog, setIsShowingCheckinDialog] = useState(false);
  const [hasBeenInView, setHasBeenInView] = useState(inView);
  const handleCheckIn = () => {
    setIsShowingCheckinDialog(true);
  };

  const skip = !goalId || !hasBeenInView;
  const { data } = useQuery<
    GetArtifactComponentQueryQuery,
    GetArtifactComponentQueryQueryVariables
  >(getArtifactComponentQuery, {
    variables: { artifactId: goalId || 0 },
    skip,
  });
  const artifact = data?.artifact;

  useEffect(() => {
    if (inView) {
      setHasBeenInView(true);
    }
  }, [inView]);

  if (!goalId) {
    return null;
  }

  return (
    <>
      {isShowingCheckinDialog &&
        artifact &&
        artifact.__typename === "GoalArtifactNode" && (
          <CheckinDialog
            artifact={artifact}
            onClose={(response) => {
              if (response) {
                setUpdatedProgress(true);
              }
              setIsShowingCheckinDialog(false);
            }}
            defaultForm={{
              message: suggestedMessage,
            }}
          />
        )}
      <div className="flex flex-col gap-4" ref={ref}>
        <div>
          {artifact ? (
            <Artifact artifact={artifact} />
          ) : (
            <div className="flex gap-3">
              <div className="text-gray-500 bg-gray-100 animate-pulse rounded-full h-6 w-6" />
              <div className="text-gray-500 bg-gray-100 animate-pulse rounded-md h-6 flex-1" />
            </div>
          )}
        </div>
        {!updatedProgress && (
          <>
            {suggestedMessage && (
              <div>
                <div className="font-medium mb-1">Suggested Update</div>
                <div className="text-gray-500">{suggestedMessage}</div>
              </div>
            )}
            {showCheckinButton && (
              <div className="flex items-center gap-2">
                <div>
                  <Button
                    theme={buttonTheme.primary}
                    text="Edit & Update progress"
                    onClick={handleCheckIn}
                    disabled={
                      artifact &&
                      artifact.__typename === "GoalArtifactNode" &&
                      artifact.state === GoalState.Closed
                    }
                  />
                </div>
                {artifact &&
                  artifact.__typename === "GoalArtifactNode" &&
                  artifact.state === GoalState.Closed && (
                    <div className="text-gray-500 italic">
                      This {label("goal")} is closed and cannot be updated.
                    </div>
                  )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default AskAIEmbedGoalProgressUpdate;
